import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import {
  DollarSign,
  CheckCircle,
  AlertCircle,
  Info,
  Check,
  Lock,
  Globe,
  Key,
  Copy,
  Eye,
  EyeOff,
  Shield,
  RefreshCw,
  User,
  Zap
} from 'react-feather';
import { getAvailableCurrencies, getCurrencyInfo, formatCurrencyWithConversion } from '../utils/currency';
import { SWYCHR_MARKETS } from '../utils/swychrMarkets';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';
import './Settings.css';

const CURRENCIES = getAvailableCurrencies();

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { language, setLanguage, languages, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('preferences'); // 'preferences' | 'security' | 'api'
  const [currency, setCurrency] = useState(user?.currency || 'USD');
  const [countryCode, setCountryCode] = useState(user?.country_code || '');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Copy API key feedback
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    if (user?.currency) {
      setCurrency(getCurrencyInfo(user.currency) ? user.currency : 'USD');
    }
    setCountryCode(user?.country_code || '');
  }, [user]);

  const getUserInitials = () => {
    if (!user) return 'NB';
    const name = user.username || user.name || user.email || '';
    if (name) {
      const parts = name.trim().split(/[\s_.-]+/);
      if (parts.length >= 2 && parts[0] && parts[1]) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    return 'NB';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    setSuccess(false);

    try {
      if (selectedLanguage !== language) {
        setLanguage(selectedLanguage);
      }
      await authAPI.updateProfile({ currency, country_code: countryCode || null, language: selectedLanguage });
      setSuccess(true);
      setMsg(t('msg_preferences_saved', 'Preferences updated successfully!'));
      await refreshUser({ force: true });
    } catch (err) {
      setSuccess(false);
      setMsg(err.response?.data?.error || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMsg('');
    setPasswordSuccess(false);

    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMsg('All password fields are required');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg('New password must be at least 6 characters');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordMsg('New password must be different from current password');
      setPasswordLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess(true);
      setPasswordMsg('Password changed successfully!');
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordSuccess(false);
      setPasswordMsg(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const copyApiKey = () => {
    const key = user?.api_key || 'nb_live_' + (user?.id ? user.id * 8923489 : 'user_key_771829');
    navigator.clipboard?.writeText(key);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const currentCurrencyObj = getCurrencyInfo(currency);
  const userBalanceDisplay = formatCurrencyWithConversion(user?.balance || 0, user?.currency || 'USD');

  return (
    <div className="settings-page-container">
      {/* Page Header */}
      <div>
        <h1 className="page-title">Account Settings</h1>
        <p className="page-subtitle">Manage your profile, localization, security, and developer credentials</p>
      </div>

      {/* User Hero Card */}
      <div className="settings-hero-card">
        <div className="settings-hero-bubble settings-hero-bubble-1" />
        <div className="settings-hero-bubble settings-hero-bubble-2" />

        <div className="settings-profile-info">
          <div className="settings-avatar-circle">
            {getUserInitials()}
          </div>
          <div className="settings-user-meta">
            <div className="settings-user-name">
              <span>{user?.username || 'NiceBooster User'}</span>
              <span className="settings-role-badge">
                {user?.role === 'admin' ? '🛡 Administrator' : 'Verified Member'}
              </span>
            </div>
            <div className="settings-user-email">
              <span>{user?.email || 'user@nicebooster.com'}</span>
            </div>
          </div>
        </div>

        <div className="settings-hero-stats">
          <div className="settings-stat-pill">
            <span className="settings-stat-pill-label">Wallet Balance</span>
            <span className="settings-stat-pill-value">{userBalanceDisplay}</span>
          </div>

          <button
            type="button"
            onClick={() => refreshUser({ force: true })}
            className="btn btn-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.18)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: 12,
              backdropFilter: 'blur(10px)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              padding: '10px 14px'
            }}
          >
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Segmented Navigation Tabs */}
      <div className="settings-tabs-bar">
        <button
          type="button"
          onClick={() => setActiveTab('preferences')}
          className={`settings-tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
        >
          <Globe size={16} /> {t('tab_preferences', 'Preferences & Currency')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('security')}
          className={`settings-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
        >
          <Lock size={16} /> {t('tab_security', 'Security & Password')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('api')}
          className={`settings-tab-btn ${activeTab === 'api' ? 'active' : ''}`}
        >
          <Key size={16} /> {t('tab_api', 'API & Reseller Key')}
        </button>
      </div>

      {/* TAB 1: Preferences & Currency */}
      {activeTab === 'preferences' && (
        <form onSubmit={handleSave} style={{ maxWidth: 680 }}>
          {msg && (
            <div className={`alert ${success ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{msg}</span>
              </div>
            </div>
          )}

          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon-box">
                <Globe size={20} />
              </div>
              <div>
                <h3 className="settings-card-title">Localization & Currency</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Set your location and preferred currency for orders, balance, and payment methods
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>Primary Country</label>
              <select
                className="form-input"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={loading}
                style={{ fontSize: 14 }}
              >
                <option value="">Select your country</option>
                {SWYCHR_MARKETS.map((market) => (
                  <option key={market.countryCode} value={market.countryCode}>
                    {market.flag || ''} {market.label} ({market.currency})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                Mobile Money and localized payment options will default to this country.
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>{t('field_currency', 'Account Display Currency')}</label>
              <select
                className="form-input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={loading}
                style={{ fontSize: 14 }}
              >
                {CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.code} — {curr.name}
                  </option>
                ))}
              </select>
              {currentCurrencyObj && (
                <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 8, fontWeight: 600 }}>
                  Active Currency: {currentCurrencyObj.symbol} {currentCurrencyObj.code} ({currentCurrencyObj.name})
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>{t('field_language', 'Preferred Language')}</label>
              <select
                className="form-input"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={loading}
                style={{ fontSize: 14 }}
              >
                {languages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.flag} {l.nativeName} ({l.name})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                {t('field_language_desc', 'Select the display language for the platform interface and notifications.')}
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              type="submit"
              disabled={loading || (currency === user?.currency && countryCode === (user?.country_code || '') && selectedLanguage === language)}
              style={{
                borderRadius: 12,
                padding: '14px 24px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {loading ? (
                <>
                  <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }} />
                  {t('btn_saving_preferences', 'Updating Preferences...')}
                </>
              ) : (
                <>
                  <Check size={16} /> {t('btn_save_preferences', 'Save Preferences')}
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* TAB 2: Security & Password */}
      {activeTab === 'security' && (
        <form onSubmit={handlePasswordChange} style={{ maxWidth: 680 }}>
          {passwordMsg && (
            <div className={`alert ${passwordSuccess ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {passwordSuccess ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                <span>{passwordMsg}</span>
              </div>
            </div>
          )}

          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon-box" style={{ background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' }}>
                <Lock size={20} />
              </div>
              <div>
                <h3 className="settings-card-title">Change Password</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Keep your account safe by using a strong, unique password
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 18 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>Current Password</label>
              <div className="settings-password-wrap">
                <input
                  className="form-input"
                  type={showOldPassword ? 'text' : 'password'}
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={passwordLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="settings-password-toggle"
                >
                  {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 18 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>New Password</label>
              <div className="settings-password-wrap">
                <input
                  className="form-input"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  disabled={passwordLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="settings-password-toggle"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label" style={{ fontWeight: 700 }}>Confirm New Password</label>
              <div className="settings-password-wrap">
                <input
                  className="form-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Repeat new password"
                  autoComplete="new-password"
                  disabled={passwordLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="settings-password-toggle"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              type="submit"
              disabled={passwordLoading || !passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
              style={{
                borderRadius: 12,
                padding: '14px 24px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {passwordLoading ? (
                <>
                  <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, borderTopColor: '#fff' }} />
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock size={16} /> Update Password
                </>
              )}
            </button>

            {/* Security checklist */}
            <div className="settings-security-box">
              <div className="settings-security-title">
                <Shield size={14} style={{ color: 'var(--accent)' }} /> Password Recommendations
              </div>
              <div className="settings-security-item">
                <span>✓</span> Minimum 6 characters (recommended 8+ with mixed letters and numbers)
              </div>
              <div className="settings-security-item">
                <span>✓</span> Never share your account credentials with anyone
              </div>
              <div className="settings-security-item">
                <span>✓</span> Avoid using passwords that you use on other websites
              </div>
            </div>
          </div>
        </form>
      )}

      {/* TAB 3: API & Reseller Key */}
      {activeTab === 'api' && (
        <div style={{ maxWidth: 680 }}>
          <div className="settings-card">
            <div className="settings-card-header" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  className="settings-card-icon-box"
                  style={{
                    background: (user?.role === 'admin' || user?.role === 'super_admin' || user?.is_tester)
                      ? 'rgba(16, 185, 129, 0.12)'
                      : 'rgba(239, 68, 68, 0.12)',
                    color: (user?.role === 'admin' || user?.role === 'super_admin' || user?.is_tester)
                      ? '#10b981'
                      : '#ef4444'
                  }}
                >
                  <Key size={20} />
                </div>
                <div>
                  <h3 className="settings-card-title">Developer & Reseller API</h3>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Connect external websites, stores, or SMM panels via standard v2 API
                  </div>
                </div>
              </div>

              {(user?.role === 'admin' || user?.role === 'super_admin' || user?.is_tester) ? (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    background: 'rgba(16, 185, 129, 0.14)',
                    color: '#10b981',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  ● Active {user?.role === 'admin' || user?.role === 'super_admin' ? '(Admin)' : '(Verified Reseller)'}
                </span>
              ) : (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: 20,
                    background: 'rgba(239, 68, 68, 0.12)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.28)',
                    fontSize: 12,
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }}
                >
                  ● Inactive (Default)
                </span>
              )}
            </div>

            {/* Check if user is admin or approved reseller */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.is_tester) ? (
              <div>
                <div className="form-group" style={{ marginBottom: 18 }}>
                  <label className="form-label" style={{ fontWeight: 700 }}>Your API Key</label>
                  <div className="settings-api-key-box">
                    <span className="settings-api-key-text">
                      {user?.api_key || 'nb_live_' + (user?.id ? user.id * 8923489 : 'user_api_key_771829')}
                    </span>
                    <button
                      type="button"
                      onClick={copyApiKey}
                      className="settings-api-copy-btn"
                    >
                      {copiedKey ? <Check size={14} /> : <Copy size={14} />}
                      {copiedKey ? 'Copied!' : 'Copy Key'}
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Keep your API key private. Any automated orders placed using your API key are deducted directly from your wallet balance.
                  </div>
                </div>

                <div className="settings-security-box">
                  <div className="settings-security-title">
                    <Zap size={14} style={{ color: '#10b981' }} /> API Endpoint & Documentation
                  </div>
                  <code style={{ display: 'block', padding: '10px 14px', background: 'var(--bg-card)', borderRadius: 8, fontSize: 13, border: '1px solid var(--border)' }}>
                    POST https://nicebooster.com/api/v2
                  </code>
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                    Supported Actions: <code>add</code> (place order), <code>status</code> (check order), <code>services</code> (list catalog), <code>balance</code> (wallet check).
                  </div>
                </div>
              </div>
            ) : (
              /* Inactive State for Standard Users */
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    padding: '16px 18px',
                    borderRadius: 14,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    marginBottom: 16
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>
                    API Reseller Access is Restricted
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
                    Reseller API keys are designed for developers, digital agency partners, and external website owners who automate high-volume order placement. To protect personal accounts against unauthorized automated deductions, API access is <strong>inactive by default</strong>.
                  </p>
                </div>

                <div
                  style={{
                    padding: '16px 18px',
                    borderRadius: 14,
                    background: 'rgba(37, 99, 235, 0.08)',
                    border: '1px solid rgba(37, 99, 235, 0.22)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                    Want to sell NiceBooster services on your own website or bot?
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Admins can activate your API key upon request. Contact our team with your business name or website details.
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <a
                      href={`https://wa.me/237671234567?text=${encodeURIComponent(`Hello Admin, I would like to request Reseller API activation for my NiceBooster account: ${user?.email || user?.username}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-primary btn-sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        borderRadius: 10,
                        padding: '10px 16px',
                        textDecoration: 'none',
                        fontWeight: 700
                      }}
                    >
                      <Zap size={14} /> Request Activation via WhatsApp
                    </a>

                    <a
                      href="/dashboard/support"
                      className="btn btn-outline btn-sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        borderRadius: 10,
                        padding: '10px 16px',
                        fontWeight: 700
                      }}
                    >
                      Open Support Ticket
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ads display */}
      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />
      <AdsDisplay adSlot="banner-320x50" className="banner" />
    </div>
  );
}
